import type { PluginStudyContext } from '../types';

/**
 * Guess which chiro body-part region (1..7) is most relevant for the
 * currently displayed study. Used to preselect the picker's region tab.
 *
 * Two sources: the DICOM (0018,0015) BodyPartExamined tag if the modality
 * exposed it, else free-text study/series description matching. Returns
 * null when nothing plausible is found — the picker falls back to the
 * first tab (Skull) so the tool remains usable.
 *
 * Body-part IDs mirror BODY_PARTS in types.ts:
 *   1 Skull  2 Cervical  3 Thoracic  4 Lumbar
 *   5 Lower Ext.  6 Upper Ext.  7 Special
 */
export function guessBodyPart(ctx: PluginStudyContext | null): number | null {
  if (!ctx) return null;
  const bp = (ctx.bodyPart || '').toUpperCase();
  const desc = `${ctx.studyDescription || ''} ${ctx.seriesDescription || ''}`.toUpperCase();

  // 1. DICOM BodyPartExamined tag (defined values, most reliable).
  if (bp) {
    if (/SKULL|HEAD|CRANIUM/.test(bp)) return 1;
    if (/CSPINE|CERVICAL|NECK/.test(bp)) return 2;
    if (/TSPINE|THORACIC|CHEST/.test(bp)) return 3;
    if (/LSPINE|LUMBAR|LSSPINE|SISPINE|SACRUM/.test(bp)) return 4;
    if (/PELVIS|HIP|KNEE|FEMUR|TIBIA|FIBULA|ANKLE|FOOT|LEG/.test(bp)) return 5;
    if (/SHOULDER|CLAVICLE|HUMERUS|ARM|ELBOW|WRIST|HAND|FOREARM/.test(bp)) return 6;
  }

  // 2. Description fallback — free-text match. Order matters: lumbar
  // before spine (a "lumbar spine" desc would false-match on "spine").
  if (/SKULL|CRANIUM|HEAD/.test(desc)) return 1;
  if (/CERVICAL|C-?SPINE|NECK|ATLAS|AXIS|C[1-7]\b/.test(desc)) return 2;
  if (/THORACIC|T-?SPINE|DORSAL|CHEST/.test(desc)) return 3;
  if (/LUMBAR|L-?SPINE|SI JOINT|SACRO?ILIAC|LSS/.test(desc)) return 4;
  if (/PELVIS|HIP|KNEE|FEMUR|PATELLA|TIBIA|ANKLE|FOOT|LEG/.test(desc)) return 5;
  if (/SHOULDER|CLAVICLE|HUMERUS|ELBOW|WRIST|HAND|ARM|FOREARM/.test(desc)) return 6;
  if (/SPINE/.test(desc)) return 4; // generic "spine" → lumbar as best guess

  return null;
}
